import { sel } from "../selectors";
import { DEFAULT_STAGE_VIEWPORT, SAMPLES_BASE, Scene } from "./types";

// Lesson: Reports from audio → Audio types. Fixture `audioTypes`: the officer
// uploads the Valley Mart witness interview (sample
// interaction-witness-interview.mp3), switches the row from its default
// Narrative to Interaction, names the two speakers, and uploads; the finished
// draft names the witness and the Transcription tab shows two separated voices.
const ROW = '[data-file-index="0"]';
const SPEAKER_1 = `${ROW} .space-y-2 > div:nth-child(1)`;
const SPEAKER_2 = `${ROW} .space-y-2 > div:nth-child(2)`;

const scene: Scene = {
  id: "narrative-vs-interaction-audio",
  title: "Audio types",
  startRoute: "/cases",
  fixture: "audioTypes",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "Every recording you upload is one of two types. A Narrative is you alone, dictating the report. An Interaction is a conversation: an interview, a traffic stop, body-cam audio. They go through different transcription engines, so the type matters.",
      actions: [
        { type: "waitFor", target: sel("newCaseDropdown") },
        { type: "spotlight", target: null },
        { type: "wait", ms: 1500 },
      ],
    },
    {
      id: "add-file",
      narration:
        "I'll click New Case, choose Upload Files, and add a recording of a witness interview.",
      actions: [
        { type: "moveTo", target: `${sel("newCaseDropdown")} button` },
        { type: "wait", ms: 300 },
        { type: "click", target: `${sel("newCaseDropdown")} button` },
        { type: "waitFor", target: sel("newCaseUpload") },
        { type: "wait", ms: 300 },
        { type: "click", target: sel("newCaseUpload") },
        { type: "waitFor", target: sel("uploadDropzone") },
        { type: "moveTo", target: sel("uploadDropzone") },
        { type: "wait", ms: 600 },
        {
          type: "attachFile",
          target: `${sel("uploadDropzone")} input[type="file"]`,
          url: `${SAMPLES_BASE}/interaction-witness-interview.mp3`,
          name: "interaction-witness-interview.mp3",
          mime: "audio/mpeg",
        },
        { type: "waitFor", target: sel("uploadTypeInteraction") },
        { type: "wait", ms: 800 },
      ],
    },
    {
      id: "pick-type",
      narration:
        "Each file row has a Narrative button and an Interaction button. A short recording defaults to Narrative, so always check the row before you upload. This is a conversation between two people, so I'll click Interaction.",
      actions: [
        { type: "moveTo", target: `${ROW} ${sel("uploadTypeNarrative")}` },
        { type: "spotlight", target: `${ROW} ${sel("uploadTypeNarrative")}` },
        // "I'll click Interaction" is spoken at about 11.2 s of 12.9 s.
        { type: "wait", ms: 9800 },
        { type: "spotlight", target: null },
        { type: "click", target: `${ROW} ${sel("uploadTypeInteraction")}` },
        { type: "waitFor", target: sel("uploadAddSpeaker") },
        { type: "wait", ms: 500 },
      ],
    },
    {
      id: "engines",
      narration:
        "A narrative is one voice, transcribed as a block of text, and the report is written from what you said, in your perspective. An interaction is transcribed with speaker separation and timestamps, so the AI can write what happened and quote the people speaking.",
      actions: [
        { type: "moveTo", target: sel("uploadSpeakerInformation") },
        { type: "spotlight", target: sel("uploadSpeakerInformation") },
        { type: "wait", ms: 6000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "speakers",
      narration:
        "Under Speaker Information, click Add Speaker for each person, then pick a role and add a name if you have one. I'll add myself as the officer, and Daniel Reyes as the witness. Names you enter here carry through to the draft.",
      actions: [
        // "click Add Speaker" at ~2 s, "I'll add myself as the officer" at
        // 6.8 s, "Daniel Reyes as the witness" at ~9.3 s of 13.4 s.
        { type: "wait", ms: 1600 },
        { type: "click", target: sel("uploadAddSpeaker") },
        { type: "waitFor", target: `${SPEAKER_1} input` },
        { type: "click", target: sel("uploadAddSpeaker") },
        { type: "waitFor", target: `${SPEAKER_2} input` },
        { type: "wait", ms: 3300 },
        { type: "type", target: `${SPEAKER_1} input`, text: "Officer Ramirez", cps: 20 },
        { type: "selectOption", target: `${SPEAKER_1} select`, value: "officer" },
        { type: "wait", ms: 700 },
        { type: "type", target: `${SPEAKER_2} input`, text: "Daniel Reyes", cps: 20 },
        { type: "selectOption", target: `${SPEAKER_2} select`, value: "witness" },
        { type: "wait", ms: 800 },
      ],
    },
    {
      id: "languages",
      narration:
        "If the conversation wasn't entirely in English, open Spoken Languages and add each language used. This one was all English, so I'll leave it blank. Anything the recording doesn't say goes in Additional Context.",
      actions: [
        { type: "scrollIntoView", target: sel("uploadSpokenLanguages") },
        { type: "moveTo", target: sel("uploadSpokenLanguages") },
        { type: "spotlight", target: sel("uploadSpokenLanguages") },
        { type: "wait", ms: 6500 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "upload",
      narration:
        "Then I'll click Upload. The interview is transcribed with the two voices separated, and the draft is written from it.",
      actions: [
        { type: "scrollIntoView", target: sel("uploadSubmit") },
        { type: "click", target: sel("uploadSubmit") },
        { type: "waitFor", target: sel("editorProcessing"), timeoutMs: 15000 },
        { type: "wait", ms: 4000 },
        { type: "fixture", name: "completeLatestCase" },
      ],
    },
    {
      id: "draft",
      narration:
        "Here's the draft. Because I named the speakers, it says Reyes instead of Speaker 2. The Transcription tab shows each line with who said it, and you can rename speakers there later.",
      actions: [
        { type: "waitFor", target: sel("reportScore"), timeoutMs: 15000 },
        { type: "wait", ms: 2500 },
        { type: "click", target: sel("editorTabTranscription") },
        { type: "waitFor", target: '[data-segment-id="1"]' },
        { type: "wait", ms: 3000 },
      ],
    },
  ],
};

export default scene;
